import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const OrderSettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [orderDeliveryVerification, setOrderDeliveryVerification] = useState(false);
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(0);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(true);
  const [freeDeliveryResponsibility, setFreeDeliveryResponsibility] = useState('Admin');
  const [freeDeliveryOver, setFreeDeliveryOver] = useState(1000);
  const [refundOrderValidity, setRefundOrderValidity] = useState(5);
  const [guestCheckout, setGuestCheckout] = useState(false);

  useEffect(() => {
    const o = settings.custom?.orderSettings;
    if (!o) return;
    if (o.orderDeliveryVerification !== undefined) setOrderDeliveryVerification(o.orderDeliveryVerification);
    if (o.minimumOrderAmount !== undefined) setMinimumOrderAmount(o.minimumOrderAmount);
    if (o.showBillingAddress !== undefined) setShowBillingAddress(o.showBillingAddress);
    if (o.freeDelivery !== undefined) setFreeDelivery(o.freeDelivery);
    if (o.freeDeliveryResponsibility !== undefined) setFreeDeliveryResponsibility(o.freeDeliveryResponsibility);
    if (o.freeDeliveryOver !== undefined) setFreeDeliveryOver(o.freeDeliveryOver);
    if (o.refundOrderValidity !== undefined) setRefundOrderValidity(o.refundOrderValidity);
    if (o.guestCheckout !== undefined) setGuestCheckout(o.guestCheckout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleOrderDeliveryVerificationChange = (event) => {
    setOrderDeliveryVerification(event.target.checked);
  };

  const handleMinimumOrderAmountChange = (event) => {
    setMinimumOrderAmount(event.target.value);
  };

  const handleShowBillingAddressChange = (event) => {
    setShowBillingAddress(event.target.checked);
  };

  const handleFreeDeliveryChange = (event) => {
    setFreeDelivery(event.target.checked);
  };

  const handleFreeDeliveryResponsibilityChange = (event) => {
    setFreeDeliveryResponsibility(event.target.value);
  };

  const handleFreeDeliveryOverChange = (event) => {
    setFreeDeliveryOver(event.target.value);
  };

  const handleRefundOrderValidityChange = (event) => {
    setRefundOrderValidity(event.target.value);
  };

  const handleGuestCheckoutChange = (event) => {
    setGuestCheckout(event.target.checked);
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        custom: {
          orderSettings: {
            orderDeliveryVerification,
            minimumOrderAmount,
            showBillingAddress,
            freeDelivery,
            freeDeliveryResponsibility,
            freeDeliveryOver,
            refundOrderValidity,
            guestCheckout,
          },
        },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Order Settings
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Order Delivery Verification
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={orderDeliveryVerification} onChange={handleOrderDeliveryVerificationChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Minimum order amount
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={minimumOrderAmount > 0} onChange={() => setMinimumOrderAmount(minimumOrderAmount > 0 ? 0 : 10)} />
            {minimumOrderAmount > 0 && (
              <TextField
                type="number"
                value={minimumOrderAmount}
                onChange={handleMinimumOrderAmountChange}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Show Billing Address In Checkout
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={showBillingAddress} onChange={handleShowBillingAddressChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Free Delivery
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={freeDelivery} onChange={handleFreeDeliveryChange} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="free-delivery-responsibility-select-label">Free Delivery Responsibility</InputLabel>
              <Select
                labelId="free-delivery-responsibility-select-label"
                id="free-delivery-responsibility-select"
                value={freeDeliveryResponsibility}
                onChange={handleFreeDeliveryResponsibilityChange}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Vendor">Vendor</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Free Delivery Over($)
              <InfoIcon color="primary" />
            </Typography>
            <TextField
              type="number"
              value={freeDeliveryOver}
              onChange={handleFreeDeliveryOverChange}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Refund Order Validity (Days)"
              type="number"
              value={refundOrderValidity}
              onChange={handleRefundOrderValidityChange}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.primary">
              Guest checkout
              <InfoIcon color="primary" />
            </Typography>
            <Switch checked={guestCheckout} onChange={handleGuestCheckoutChange} />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" fullWidth onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Grid>
        </Grid>

        <SaveSnackbar snack={snack} onClose={closeSnack} />
      </CardContent>
    </Card>
  );
};

export default OrderSettings;
